import { Button } from "@/components/ui/button";
import { useBrandStore } from "../stores/brandStore";
import type { BrandListItem } from "../types/brand.types";

const COLUMNS = [
  { key: "companyName", label: "기존 배차 업체" },
  { key: "name", label: "표시명" },
  { key: "host", label: "호스트" },
  { key: "phone", label: "연락처" },
  { key: "isActive", label: "상태" },
] as const;

type ColumnKey = (typeof COLUMNS)[number]["key"];

interface BrandTableProps {
  data: BrandListItem[] | undefined;
  isLoading: boolean;
  error?: Error | null;
}

export function BrandTable({ data, isLoading, error }: BrandTableProps) {
  const openDrawerForEdit = useBrandStore((s) => s.openDrawerForEdit);

  const items = data ?? [];
  const totalCount = items.length;

  const renderCell = (brand: BrandListItem, key: ColumnKey) => {
    switch (key) {
      case "name":
        return (
          <span className="font-medium text-blue-600">{brand.name}</span>
        );
      case "host":
        return <span className="font-medium text-gray-900">{brand.host}</span>;
      case "isActive":
        return brand.isActive ? (
          <span className="inline-block rounded-full bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
            활성
          </span>
        ) : (
          <span className="inline-block rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
            비활성
          </span>
        );
      default:
        return brand[key] ?? "-";
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="shrink-0 border-b border-gray-200 bg-white p-3">
        <span className="text-base font-medium text-gray-900">
          브랜드 목록
        </span>
        <span className="ml-2 text-xs text-gray-500">총 {totalCount}건</span>
      </div>

      <div className="flex-1 overflow-auto bg-white">
        <table className="w-full">
          <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="h-10 px-4 py-3 text-left text-xs font-medium uppercase text-gray-500"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="h-32 text-center text-xs text-gray-400"
                >
                  조회 중...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="h-32 px-4 text-center text-xs text-red-600"
                >
                  <p className="font-medium">브랜드 목록을 불러오지 못했습니다</p>
                  <p className="mt-1 text-gray-500">{error.message}</p>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  className="h-32 text-center text-xs text-gray-400"
                >
                  등록된 브랜드가 없습니다
                </td>
              </tr>
            ) : (
              items.map((brand) => (
                <tr
                  key={brand.idx}
                  className="h-10 cursor-pointer hover:bg-gray-50"
                  onDoubleClick={() => openDrawerForEdit(brand.idx)}
                >
                  {COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      className="px-4 py-2 text-xs text-gray-700"
                    >
                      {renderCell(brand, col.key)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-gray-50 px-3 py-2">
        <span className="text-xs text-gray-700">
          {error ? "조회 실패" : `총 ${totalCount}건`}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 py-1 text-xs"
          disabled
        >
          전체 조회
        </Button>
      </div>
    </div>
  );
}
